import { writeModuleIndex } from "../shared/module-index.ts";
import { pagePath } from "./paths.ts";

/**
 * Disk-writing counterpart to render.ts's pure renderPage — kept separate
 * for the same reason Calendar/Contacts/Tasks split theirs out, so render.ts
 * stays a pure, DOM-free, unit-testable template.
 */

export interface OneNotePageEntry {
    notebook: string;
    notebookKey: string;
    section: string;
    sectionKey: string;
    title: string;
    pageKey: string;
}

/**
 * Rebuilds `onenote/index.html` — one row per page across every notebook and
 * section — from the pages exportOneNote already wrote this run. Each row
 * links to that page's own standalone .html.
 */
export async function renderOneNoteIndex(
    root: FileSystemDirectoryHandle,
    pages: readonly OneNotePageEntry[]
): Promise<void> {
    const sorted = [...pages].sort(
        (a, b) => a.notebook.localeCompare(b.notebook) || a.section.localeCompare(b.section)
    );

    await writeModuleIndex(root, {
        moduleId: "onenote",
        itemKind: "html",
        title: "OneNote",
        subtitle: `${sorted.length} page${sorted.length === 1 ? "" : "s"} exported`,
        columns: [
            { key: "title", label: "Page" },
            { key: "notebook", label: "Notebook" },
            { key: "section", label: "Section" }
        ],
        rows: sorted.map((p) => ({
            cells: {
                title: p.title || "(untitled page)",
                notebook: p.notebook,
                section: p.section
            },
            href: pagePath(p.notebookKey, p.sectionKey, p.pageKey).replace(/^onenote\//, "")
        })),
        emptyMessage: "No pages exported yet.",
        searchPlaceholder: "Filter pages…"
    });
}
